import { useCallback, useLayoutEffect, useRef } from "react";

import type { TimelinePlayerBridge } from "../bridge";
import { adjacentFrameTime, boundaryFrameTime, frameSeekTime, FrameTimestamps } from "../engine/frame-timestamps";

type FrameNavigation = -1 | 1 | "first" | "last";

/** Frame navigation belongs to the source video, independently of annotations and tracking. */
export function useFrameStepping(source: string, bridge: TimelinePlayerBridge) {
  const request = useRef<(direction: FrameNavigation) => void>(() => {});

  useLayoutEffect(() => {
    const timestamps = new FrameTimestamps(source);
    const pending: FrameNavigation[] = [];
    let active = true;
    let loading = false;
    let queuedFrom: number | undefined;

    const flush = async () => {
      loading = true;
      try {
        const frames = await timestamps.load();
        const from = queuedFrom;
        // A different source, resumed playback, or manual seek supersedes the
        // keys queued while metadata was loading.
        if (!active || from === undefined || !bridge.isPaused() || bridge.getCurrentTime() !== from) return;
        const duration = bridge.getDuration?.();
        let target = from;
        for (const direction of pending) {
          const next =
            typeof direction === "number"
              ? adjacentFrameTime(frames, target, direction, duration)
              : boundaryFrameTime(frames, direction, duration);
          if (next !== undefined) target = frameSeekTime(next);
        }
        if (target !== from) bridge.seek(target);
      } catch (error) {
        if (active) console.warn("Could not read video frame timestamps for frame stepping.", error);
      } finally {
        pending.length = 0;
        queuedFrom = undefined;
        loading = false;
      }
    };

    request.current = (direction) => {
      const wasPlaying = !bridge.isPaused();
      bridge.pause();
      const from = bridge.getCurrentTime();
      if (wasPlaying || queuedFrom !== from) {
        pending.length = 0;
        queuedFrom = from;
      }
      pending.push(direction);
      if (!loading) void flush();
    };
    return () => {
      active = false;
      request.current = () => {};
      pending.length = 0;
      timestamps.dispose();
    };
  }, [source, bridge]);

  return useCallback((direction: FrameNavigation) => request.current(direction), []);
}
